import type { AxiosInstance } from 'axios';

import { axiosCache } from './axios_cache';

/** A configured service base URL as a case-insensitive pattern, else the fallback. */
const servicePattern = (base: unknown, fallback: RegExp): RegExp =>
  typeof base === 'string' && base ? new RegExp(base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i') : fallback;

/**
 * Installs the global axios response cache when the host config enables it (`axiosCacheEnabled === '1'`), with the
 * services it must never answer for. Returns whether it was installed. Shared by the app's boot (main.tsx) and the
 * component tests that need the app's real request behaviour.
 */
export const installAppAxiosCache = (instance: AxiosInstance, config: Record<string, unknown> | undefined): boolean => {
  if (config?.axiosCacheEnabled !== '1') return false;
  // The blog service is excluded WHOLESALE. The cache is installed with no
  // method filter, so it keys POST/PUT too — and a multipart body serialises to
  // `{}` in the cache key, which would collapse two hero uploads inside the TTL
  // onto one CDN URL. GETs are just as wrong: a list refetch right after a
  // publish would serve the pre-publish articles.
  //
  // Derived from the configured base URL rather than restating its path: the helm
  // chart renders `blogApiUrl` per environment from spark_blog_api_url, so a
  // hard-coded `/spark-blog/` would silently stop matching the day that URL changes
  // shape (it already does not match the `/api/spark-blog-main/v1` prefix that
  // spark-test1 serves its main on) — and the failure is silent data corruption,
  // not an error. The literal stays as the fallback for a build with no blog
  // configured, where no blog request is issued at all.
  const blogPattern = servicePattern(config.blogApiUrl, /\/spark-blog\//i);
  // The creatives service (spark-creatives) likewise, for the same two reasons: its state changes under the reader
  // (jobs progress, a created or extracted art style), so a re-read inside the TTL must reach the service, and its
  // POSTs (create a style, start an extraction, enqueue a draw) must never collapse onto one cached answer. Measured:
  // an empty brand's Art Style page re-read its style list right after creating the first style, got the cached empty
  // list, lost the new style and stayed on its skeleton (production, brand 60575).
  const creativesPattern = servicePattern(config.creativesApiUrl, /\/spark-creatives\//i);
  // The reads the Website hub's Logos card shares with the Logos page: the brand's logo family (casino-creation through
  // the owner-hub proxy) and the Stats registration list (the logo-edit lock's player count). The cache answers
  // identical requests from one in-flight read, and the hub stays mounted through the page transition, so the page's
  // reads joined the hub's and the hub's unmount cancelled them under the page: "No logo set available yet" for a brand
  // with logos, and an unknown player count. Each reader must get its own answer.
  const logoFamilyPattern = /\/mlp\/c\/casino\/tenant\/[^/?]+\/logo-family/i;
  // casino-creation's by-domain lookup (a brand's internal host) the same way: the hub's Games card, the Website
  // Creatives and Game Lists pages and the local Studio's top bar each read it, and a joined read cancelled under a page
  // sent its preview and its above-the-fold and game-list reads to the public domain until the next brand switch.
  const byDomainPattern = /\/mlp\/c\/casino\/tenants\/by-domain/i;
  // Owner-hub's country filter (Settings > Website Settings > Country access): the cache keys POSTs too, so saving a
  // list the owner had saved moments earlier would be answered from the cache and never reach the service, and the
  // read after a save must see the new setting.
  const countryFilterPattern = /\/casino-instance\/country-filter/i;
  // The affiliates proxy (owner-hub `/user/affiliate/*`, the affiliates platform behind it) the same way: assigning or
  // removing an affiliate's commission plan and re-reading it inside the TTL got the pre-change read back, so the new
  // plan showed seconds late (Affiliates page) and Players kept its old commission; and a PUT repeated inside the TTL
  // (assign, remove, assign the same plan again) would be answered from the cache without reaching the platform.
  const userBase = config.userApiUrl;
  const affiliatePattern = servicePattern(
    typeof userBase === 'string' && userBase ? `${userBase.replace(/\/+$/, '')}/affiliate/` : undefined,
    /\/user\/affiliate\//i,
  );
  // The owner's notifications (owner-hub `/user/notifications`, its `/shown` and `/read`) the same way: the bell reads
  // the list again right after a shown or a read, and the cached list from before it brought the announced one back,
  // so the bell rang over and over until the TTL ran out; and a shown or read repeated inside the TTL never reached
  // Owner Hub.
  const notificationsPattern = servicePattern(
    typeof userBase === 'string' && userBase ? `${userBase.replace(/\/+$/, '')}/notifications` : undefined,
    /\/user\/notifications/i,
  );
  const statsBase = config.ownerHubStatsApiUrl;
  const playerListPattern = servicePattern(
    typeof statsBase === 'string' && statsBase ? `${statsBase}/select/data-set` : undefined,
    /\/select\/data-set/i,
  );

  axiosCache(instance, {
    ignoreUrls: [
      /\/api\/digitain/i,
      /\/config-api/i,
      /\/api\/v1\/domains\/suggestions/i,
      blogPattern,
      creativesPattern,
      logoFamilyPattern,
      byDomainPattern,
      playerListPattern,
      countryFilterPattern,
      affiliatePattern,
      notificationsPattern,
    ],
  });
  return true;
};

export default installAppAxiosCache;
